import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const resolveOpenSearchAdminBase = () => {
  if (typeof window === 'undefined' || !window.location) {
    return 'http://localhost:8012';
  }
  const { protocol, hostname, port, host } = window.location;
  if (String(port) === '3000') {
    return `${protocol}//${hostname}:8012`;
  }
  return `${protocol}//${host}/opensearch-admin-api`;
};

const defaultForm = {
  base_url: '',
  username: '',
  password: '',
  verify_ssl: false,
  index_prefix: 'products',
  embedding: {
    enabled: false,
    provider: 'none',
    model: 'text-embedding-3-small',
    dimension: 1536,
    text_fields: 'productCode,name,description,manufacturerName,manufacturerPartNumber',
    local_url: 'http://host.docker.internal:11434/api/embeddings',
    openai_base_url: 'https://api.openai.com/v1',
    openai_api_key: '',
  },
  vector_search: {
    enabled: false,
    k: 30,
  },
};

const OpenSearchConfig = () => {
  const { getAuthHeaders } = useAuth();
  const opensearchAdminBase = useMemo(() => resolveOpenSearchAdminBase(), []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [localModelInfo, setLocalModelInfo] = useState({ loading: false, model: '', dimension: null, error: '' });

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${opensearchAdminBase}/api/v1/products/opensearch/config`, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.detail || 'Failed to load OpenSearch config');
      }
      setForm((prev) => ({
        ...prev,
        base_url: data.base_url || prev.base_url,
        username: data.username || prev.username,
        verify_ssl: Boolean(data.verify_ssl),
        index_prefix: data.index_prefix || prev.index_prefix,
        embedding: {
          ...prev.embedding,
          ...(data.embedding || {}),
          text_fields: Array.isArray(data?.embedding?.text_fields)
            ? data.embedding.text_fields.join(',')
            : prev.embedding.text_fields,
        },
        vector_search: {
          ...prev.vector_search,
          ...(data.vector_search || {}),
        },
      }));
    } catch (error) {
      toast.error(error.message || 'Unable to load OpenSearch config');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, opensearchAdminBase]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (form.embedding.provider !== 'local') {
      setLocalModelInfo({ loading: false, model: '', dimension: null, error: '' });
      return;
    }
    const endpoint = String(form.embedding.local_url || '').trim();
    if (!endpoint) {
      setLocalModelInfo({ loading: false, model: '', dimension: null, error: 'Local endpoint URL is empty.' });
      return;
    }
    let healthUrl = endpoint;
    if (healthUrl.endsWith('/api/embeddings')) {
      healthUrl = healthUrl.replace(/\/api\/embeddings$/, '/health');
    } else if (!healthUrl.endsWith('/health')) {
      healthUrl = `${healthUrl.replace(/\/+$/, '')}/health`;
    }
    let cancelled = false;
    const readModelInfo = async () => {
      setLocalModelInfo((prev) => ({ ...prev, loading: true, error: '' }));
      try {
        const resp = await fetch(healthUrl);
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || `Health check failed (${resp.status})`);
        }
        if (!cancelled) {
          setLocalModelInfo({
            loading: false,
            model: String(data?.model || ''),
            dimension: Number.isFinite(Number(data?.dimension)) ? Number(data.dimension) : null,
            error: '',
          });
        }
      } catch (error) {
        if (!cancelled) {
          setLocalModelInfo({
            loading: false,
            model: '',
            dimension: null,
            error: error.message || 'Unable to detect local model',
          });
        }
      }
    };
    readModelInfo();
    return () => {
      cancelled = true;
    };
  }, [form.embedding.local_url, form.embedding.provider]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));
  const setEmbeddingField = (name, value) =>
    setForm((prev) => ({ ...prev, embedding: { ...prev.embedding, [name]: value } }));
  const setVectorField = (name, value) =>
    setForm((prev) => ({ ...prev, vector_search: { ...prev.vector_search, [name]: value } }));

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = {
        base_url: form.base_url,
        username: form.username,
        verify_ssl: Boolean(form.verify_ssl),
        index_prefix: form.index_prefix,
        embedding: {
          enabled: Boolean(form.embedding.enabled),
          provider: form.embedding.provider,
          model: form.embedding.model,
          dimension: Number(form.embedding.dimension) || 1536,
          text_fields: String(form.embedding.text_fields || '')
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean),
          local_url: form.embedding.local_url,
          openai_base_url: form.embedding.openai_base_url,
          ...(form.embedding.openai_api_key ? { openai_api_key: form.embedding.openai_api_key } : {}),
        },
        vector_search: {
          enabled: Boolean(form.vector_search.enabled),
          k: Number(form.vector_search.k) || 30,
        },
      };
      if (form.password) {
        payload.password = form.password;
      }
      const resp = await fetch(`${opensearchAdminBase}/api/v1/products/opensearch/config`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.detail || 'Failed to save OpenSearch config');
      }
      setField('password', '');
      setEmbeddingField('openai_api_key', '');
      toast.success('OpenSearch config saved');
      loadConfig();
    } catch (error) {
      toast.error(error.message || 'Failed to save OpenSearch config');
    } finally {
      setSaving(false);
    }
  };

  const downloadConfig = () => {
    const blob = new Blob([JSON.stringify(form, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'opensearch-config.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-fluid py-3">
      <div className="row g-3">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <h4 className="mb-1">OpenSearch Config Management</h4>
                  <p className="text-muted mb-0">
                    Client-specific configuration for import, embeddings, and vector search.
                  </p>
                </div>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={downloadConfig}>
                    Download Config
                  </button>
                  <button type="button" className="btn btn-outline-primary" onClick={loadConfig} disabled={loading}>
                    {loading ? 'Refreshing...' : 'Refresh'}
                  </button>
                  <button type="button" className="btn btn-primary" onClick={saveConfig} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Config'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="mb-3">OpenSearch Connection</h5>
              <div className="mb-3">
                <label className="form-label">Base URL</label>
                <input className="form-control" value={form.base_url} onChange={(e) => setField('base_url', e.target.value)} />
              </div>
              <div className="row g-2">
                <div className="col-12 col-md-6">
                  <label className="form-label">Username</label>
                  <input className="form-control" value={form.username} onChange={(e) => setField('username', e.target.value)} />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Password (optional)</label>
                  <input
                    type="password"
                    className="form-control"
                    value={form.password}
                    onChange={(e) => setField('password', e.target.value)}
                    placeholder="Leave blank to keep current password"
                  />
                </div>
              </div>
              <div className="row g-2 mt-1">
                <div className="col-12 col-md-6">
                  <label className="form-label">Index Prefix</label>
                  <input className="form-control" value={form.index_prefix} onChange={(e) => setField('index_prefix', e.target.value)} />
                </div>
                <div className="col-12 col-md-6 d-flex align-items-end">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={Boolean(form.verify_ssl)}
                      onChange={(e) => setField('verify_ssl', e.target.checked)}
                      id="verify-ssl"
                    />
                    <label className="form-check-label" htmlFor="verify-ssl">Verify SSL</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="mb-3">Vector Search</h5>
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={Boolean(form.vector_search.enabled)}
                  onChange={(e) => setVectorField('enabled', e.target.checked)}
                  id="vector-enabled"
                />
                <label className="form-check-label" htmlFor="vector-enabled">
                  Enable vector search for product queries
                </label>
              </div>
              <div className="mb-3">
                <label className="form-label">Top K candidates</label>
                <input
                  type="number"
                  min="1"
                  className="form-control"
                  value={form.vector_search.k}
                  onChange={(e) => setVectorField('k', e.target.value)}
                />
              </div>
              <div className="alert alert-info small mb-0">
                Enable embeddings and run a full import after config changes so vectors are generated per product.
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="mb-3">Embeddings</h5>
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={Boolean(form.embedding.enabled)}
                  onChange={(e) => setEmbeddingField('enabled', e.target.checked)}
                  id="embeddings-enabled"
                />
                <label className="form-check-label" htmlFor="embeddings-enabled">
                  Enable embeddings during OpenSearch import
                </label>
              </div>
              <div className="row g-2">
                <div className="col-12 col-md-4">
                  <label className="form-label">Provider</label>
                  <select
                    className="form-select"
                    value={form.embedding.provider}
                    onChange={(e) => setEmbeddingField('provider', e.target.value)}
                  >
                    <option value="none">None</option>
                    <option value="local">Local Model</option>
                    <option value="openai">OpenAI</option>
                  </select>
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">Model</label>
                  <input className="form-control" value={form.embedding.model} onChange={(e) => setEmbeddingField('model', e.target.value)} />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">Vector Dimension</label>
                  <input
                    type="number"
                    min="8"
                    className="form-control"
                    value={form.embedding.dimension}
                    onChange={(e) => setEmbeddingField('dimension', e.target.value)}
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="form-label">Fields used for embedding text (comma separated)</label>
                <input
                  className="form-control"
                  value={form.embedding.text_fields}
                  onChange={(e) => setEmbeddingField('text_fields', e.target.value)}
                />
              </div>
              {form.embedding.provider === 'local' && (
                <div className="mt-3">
                  <label className="form-label">Local embedding endpoint URL</label>
                  <input className="form-control" value={form.embedding.local_url} onChange={(e) => setEmbeddingField('local_url', e.target.value)} />
                  <div className="small mt-2">
                    {localModelInfo.loading && <span className="text-muted">Detecting local model...</span>}
                    {!localModelInfo.loading && localModelInfo.model && (
                      <span className="text-success">
                        Local model: {localModelInfo.model}
                        {localModelInfo.dimension ? ` (${localModelInfo.dimension} dims)` : ''}
                      </span>
                    )}
                    {!localModelInfo.loading && !localModelInfo.model && localModelInfo.error && (
                      <span className="text-danger">Local model not reachable: {localModelInfo.error}</span>
                    )}
                  </div>
                </div>
              )}
              {form.embedding.provider === 'openai' && (
                <div className="row g-2 mt-1">
                  <div className="col-12 col-lg-6">
                    <label className="form-label">OpenAI Base URL</label>
                    <input
                      className="form-control"
                      value={form.embedding.openai_base_url}
                      onChange={(e) => setEmbeddingField('openai_base_url', e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-lg-6">
                    <label className="form-label">OpenAI API Key (optional update)</label>
                    <input
                      type="password"
                      className="form-control"
                      value={form.embedding.openai_api_key}
                      onChange={(e) => setEmbeddingField('openai_api_key', e.target.value)}
                      placeholder="Leave blank to keep current key"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenSearchConfig;
